import { Currency } from '../types'

// Fallback exchange rates relative to USD (extended for major currencies)
const FALLBACK_RATES: Record<Currency, number> = {
  USD: 1.0,
  EUR: 0.92,
  GBP: 0.79,
  SGD: 1.34,
  JPY: 150.0,
  CHF: 0.88,
  CAD: 1.35,
  AUD: 1.52,
  CNY: 7.2,
  INR: 83.0,
  KRW: 1330.0,
  BRL: 4.95,
  MXN: 17.0,
  ZAR: 19.0,
  SEK: 10.4,
  NOK: 10.5,
  DKK: 6.8,
  HKD: 7.8,
  NZD: 1.63,
  PLN: 3.98,
  CZK: 23.4,
  HUF: 360.0,
  TRY: 31.0,
  THB: 35.8,
  MYR: 4.75,
  AED: 3.67,
}

const STORAGE_KEY_RATES = 'pairfolio-exchange-rates'
const STORAGE_KEY_RATES_FETCHED = 'pairfolio-exchange-rates-fetched'

// Live rates state — restore from localStorage if available
let liveRates: Record<Currency, number> | null = (() => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_RATES)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
})()

let lastFetched: Date | null = (() => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_RATES_FETCHED)
    return stored ? new Date(stored) : null
  } catch {
    return null
  }
})()

let fetchStatus: 'idle' | 'loading' | 'success' | 'error' = liveRates
  ? 'success'
  : 'idle'
let listeners: Array<() => void> = []

// Override rates (used when viewing a snapshot with stored rates)
let overrideRates: Record<Currency, number> | null = null

// Amount masking (privacy mode)
let amountsMasked = false

export function setAmountMasked(masked: boolean) {
  amountsMasked = masked
}

export function getAmountMasked(): boolean {
  return amountsMasked
}

/** Returns masked placeholder if privacy mode is on, otherwise null */
export function maskedValue(): string | null {
  return amountsMasked ? '•••••' : null
}

export function setOverrideRates(rates: Record<Currency, number> | null) {
  overrideRates = rates
  notifyListeners()
}

export function getOverrideRates(): Record<Currency, number> | null {
  return overrideRates
}

function notifyListeners() {
  listeners.forEach((fn) => fn())
}

export function subscribeToRates(listener: () => void) {
  listeners.push(listener)
  return () => {
    listeners = listeners.filter((fn) => fn !== listener)
  }
}

export function getRateStatus() {
  return {
    isLive: fetchStatus === 'success' && liveRates !== null,
    lastFetched,
    status: fetchStatus,
  }
}

export async function fetchLiveRates(): Promise<void> {
  if (fetchStatus === 'loading') return
  fetchStatus = 'loading'
  notifyListeners()

  // Try multiple API endpoints for resilience
  const APIs = [
    {
      url: 'https://api.exchangerate-api.com/v4/latest/USD',
      parse: (data: any) => data.rates as Record<string, number>,
    },
    {
      url: 'https://open.er-api.com/v6/latest/USD',
      parse: (data: any) => data.rates as Record<string, number>,
    },
  ]

  for (const api of APIs) {
    try {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 8000)

      const res = await fetch(api.url, { signal: controller.signal })
      clearTimeout(timeout)

      if (!res.ok) continue // Try next API

      const data = await res.json()
      const newRates: Record<string, number> = { ...api.parse(data) }

      // Ensure fallback rates are included if missing from API
      for (const [currency, rate] of Object.entries(FALLBACK_RATES)) {
        if (newRates[currency] === undefined) {
          newRates[currency] = rate
        }
      }

      liveRates = newRates as Record<Currency, number>
      lastFetched = new Date()
      fetchStatus = 'success'

      // Persist to localStorage
      try {
        localStorage.setItem(STORAGE_KEY_RATES, JSON.stringify(liveRates))
        localStorage.setItem(
          STORAGE_KEY_RATES_FETCHED,
          lastFetched.toISOString(),
        )
      } catch {
        // Storage full or unavailable — continue without persistence
      }

      notifyListeners()
      return
    } catch {
      // Continue to next API
    }
  }

  // All APIs failed — use fallback rates silently
  fetchStatus = 'error'
  notifyListeners()
}

export const EXCHANGE_RATES: Record<Currency, number> = new Proxy(
  FALLBACK_RATES,
  {
    get(target, prop: string) {
      if (liveRates && prop in liveRates) {
        return liveRates[prop as Currency]
      }
      return target[prop as Currency]
    },
  },
)

export function getExchangeRates(): Record<Currency, number> {
  return overrideRates || liveRates || FALLBACK_RATES
}

export const convertCurrency = (
  amount: number,
  from: Currency,
  to: Currency,
): number => {
  if (from === to) return amount
  const rates = getExchangeRates()
  const amountInUSD = amount / rates[from]
  return amountInUSD * rates[to]
}

export const formatCurrency = (amount: number, currency: Currency): string => {
  if (amountsMasked) return '•••••'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

export const formatRate = (rate: number): string => {
  return rate.toFixed(4)
}

/**
 * Get the calendar date that is `yearsFromNow` years in the future,
 * formatted as DD/MM/YYYY.
 */
export function futureDate(yearsFromNow: number): string {
  const d = new Date()
  d.setFullYear(d.getFullYear() + yearsFromNow)
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  return `${day}/${month}/${d.getFullYear()}`
}
